import { useEffect } from "react";

import { yupResolver } from "@hookform/resolvers/yup";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  FormControl,
  FormControlLabel,
  FormHelperText,
  FormLabel,
  InputAdornment,
  Radio,
  RadioGroup,
  Stack,
} from "@mui/material";
import PropTypes from "prop-types";
import { Controller, useForm, useWatch } from "react-hook-form";

import { CustomerSelect } from "@/features/customers";
import { EntityTypeEnum } from "@/features/outlet-chart-of-account-transactions";
import { OutletChartOfAccountSelect } from "@/features/outlet-chart-of-accounts";
import { AllOutletsSelect, OutletRoleEnum } from "@/features/outlets";
import { SupplierSelect } from "@/features/suppliers";
import {
  CustomDialogTitle,
  CustomInputField,
  CustomNumberField,
} from "@/features/ui";

import {
  DEFAULT_VALUES,
  isEntityRequiredType,
  TRANSACTION_TYPE_OPTIONS,
  VALIDATION_SCHEMA,
} from "./account-transaction-form-dialog-constants";

export const AccountTransactionFormDialog = ({
  onClose,
  onSave,
  outlet,
  defaultValues,
}) => {
  const isEditMode = Boolean(defaultValues);

  const getAllowedOutletRoles = () => {
    if (!outlet?.role) return [];
    return outlet.role === OutletRoleEnum.HUB
      ? [OutletRoleEnum.RETAIL]
      : [OutletRoleEnum.HUB];
  };

  const getOutletEntityLabel = () => {
    if (!outlet?.role) return "Outlet";
    return outlet.role === OutletRoleEnum.HUB ? "Outlet Retail" : "Outlet Hub";
  };

  const { control, handleSubmit, reset, setValue } = useForm({
    defaultValues: DEFAULT_VALUES,
    resolver: yupResolver(VALIDATION_SCHEMA),
  });

  useEffect(() => {
    if (defaultValues) {
      reset({ ...DEFAULT_VALUES, ...defaultValues });
    }
  }, [defaultValues, reset]);

  const outletChartOfAccount = useWatch({
    control,
    name: "outletChartOfAccount",
  });
  const entityType = useWatch({ control, name: "entityType" });

  const showEntityFields =
    outletChartOfAccount && isEntityRequiredType(outletChartOfAccount.type);

  const handleAccountChange = (newValue) => {
    // Clear entity fields when account type changes away from AP/AR
    const needsEntity = newValue && isEntityRequiredType(newValue.type);
    if (!needsEntity) {
      setValue("entityType", null);
      setValue("entity", null);
    }
  };

  const handleClose = () => {
    reset(DEFAULT_VALUES);
    onClose();
  };

  const onFormSubmit = (data) => {
    const result = {
      outletChartOfAccount: data.outletChartOfAccount,
      debit: data.type === TRANSACTION_TYPE_OPTIONS.DEBIT ? data.amount : 0,
      credit: data.type === TRANSACTION_TYPE_OPTIONS.CREDIT ? data.amount : 0,
      note: data.note || "",
      entityType: data.entityType || null,
      entity: data.entity || null,
    };
    onSave(result);
    handleClose();
  };

  return (
    <Dialog open onClose={handleClose} fullWidth maxWidth="sm">
      <CustomDialogTitle
        variant="h5"
        title={isEditMode ? "Edit Transaksi Akun" : "Tambah Transaksi Akun"}
        onClose={handleClose}
      />
      <DialogContent dividers>
        <Stack spacing={2}>
          <Controller
            control={control}
            name="outletChartOfAccount"
            render={({ field, fieldState }) => (
              <OutletChartOfAccountSelect
                value={field.value}
                onChange={(newValue) => {
                  field.onChange(newValue);
                  handleAccountChange(field, newValue);
                }}
                outletId={outlet?.id}
                TextFieldProps={{
                  required: true,
                  label: "Akun",
                  placeholder: "Pilih Akun",
                  error: fieldState.invalid,
                  helperText: fieldState.error?.message,
                  disabled: !outlet?.id,
                }}
              />
            )}
          />

          <Controller
            control={control}
            name="type"
            render={({ field, fieldState }) => (
              <FormControl error={fieldState.invalid} required>
                <FormLabel sx={{ fontSize: "body2.fontSize" }}>Tipe</FormLabel>
                <RadioGroup row value={field.value} onChange={field.onChange}>
                  <FormControlLabel
                    value={TRANSACTION_TYPE_OPTIONS.DEBIT}
                    control={<Radio />}
                    label="Debit"
                  />
                  <FormControlLabel
                    value={TRANSACTION_TYPE_OPTIONS.CREDIT}
                    control={<Radio />}
                    label="Kredit"
                  />
                </RadioGroup>
                {fieldState.error && (
                  <FormHelperText>{fieldState.error.message}</FormHelperText>
                )}
              </FormControl>
            )}
          />

          <Controller
            control={control}
            name="amount"
            render={({ field, fieldState }) => (
              <CustomNumberField
                label="Jumlah"
                required
                value={field.value}
                onChange={field.onChange}
                error={!!fieldState.error}
                helperText={fieldState.error?.message}
                fullWidth
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">Rp</InputAdornment>
                  ),
                }}
              />
            )}
          />

          <Controller
            control={control}
            name="note"
            render={({ field, fieldState }) => (
              <CustomInputField
                label="Catatan"
                value={field.value}
                onChange={field.onChange}
                error={!!fieldState.error}
                helperText={fieldState.error?.message}
                fullWidth
              />
            )}
          />

          {showEntityFields && (
            <>
              <Controller
                control={control}
                name="entityType"
                render={({ field, fieldState }) => (
                  <FormControl error={fieldState.invalid} required>
                    <FormLabel sx={{ fontSize: "body2.fontSize" }}>
                      Tipe Entitas
                    </FormLabel>
                    <RadioGroup
                      row
                      value={field.value || ""}
                      onChange={(event) => {
                        field.onChange(event);
                        setValue("entity", null);
                      }}
                    >
                      <FormControlLabel
                        value={EntityTypeEnum.SUPPLIER}
                        control={<Radio />}
                        label="Pemasok"
                      />
                      <FormControlLabel
                        value={EntityTypeEnum.CUSTOMER}
                        control={<Radio />}
                        label="Pelanggan"
                      />
                      <FormControlLabel
                        value={EntityTypeEnum.OUTLET}
                        control={<Radio />}
                        label={getOutletEntityLabel()}
                      />
                    </RadioGroup>
                    {fieldState.error && (
                      <FormHelperText>
                        {fieldState.error.message}
                      </FormHelperText>
                    )}
                  </FormControl>
                )}
              />

              {entityType === EntityTypeEnum.SUPPLIER && (
                <Controller
                  control={control}
                  name="entity"
                  render={({ field, fieldState }) => (
                    <SupplierSelect
                      value={field.value}
                      onChange={field.onChange}
                      TextFieldProps={{
                        required: true,
                        label: "Pemasok",
                        error: fieldState.invalid,
                        helperText: fieldState.error?.message,
                      }}
                    />
                  )}
                />
              )}

              {entityType === EntityTypeEnum.CUSTOMER && (
                <Controller
                  control={control}
                  name="entity"
                  render={({ field, fieldState }) => (
                    <CustomerSelect
                      value={field.value}
                      onChange={field.onChange}
                      TextFieldProps={{
                        required: true,
                        label: "Pelanggan",
                        error: fieldState.invalid,
                        helperText: fieldState.error?.message,
                      }}
                    />
                  )}
                />
              )}

              {entityType === EntityTypeEnum.OUTLET && (
                <Controller
                  control={control}
                  name="entity"
                  render={({ field, fieldState }) => (
                    <AllOutletsSelect
                      value={field.value}
                      onChange={field.onChange}
                      allowedRoles={getAllowedOutletRoles()}
                      TextFieldProps={{
                        required: true,
                        label: getOutletEntityLabel(),
                        error: fieldState.invalid,
                        helperText: fieldState.error?.message,
                      }}
                    />
                  )}
                />
              )}
            </>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>Batal</Button>
        <Button variant="contained" onClick={handleSubmit(onFormSubmit)}>
          Simpan
        </Button>
      </DialogActions>
    </Dialog>
  );
};

AccountTransactionFormDialog.propTypes = {
  onClose: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
  outlet: PropTypes.shape({
    id: PropTypes.string,
    role: PropTypes.string,
  }),
  defaultValues: PropTypes.object,
};
